import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from "./ui/button";
import { Trash2, Users, X, Video, Phone } from 'lucide-react';
import { Switch } from './ui/switch';
import { Combobox } from './ui/combobox';
import { CommanServices } from '@/services/commanServices';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setMeetMode } from '@/Redux/Action/organizationActions';
import axios from 'axios';
import { ApiEndPoint, persistMeetModeForNextMeet } from '@/services/apiUttiLities';
import { UNKNOWN_USER_REMOVED } from '@/utils/userDisplay';

/**
 * EmployeesPanel
 * Source of truth: department.members  →  [{id, reporting_enabled, is_leader}]
 * Display data:    /api/app-users       →  [{id, name, email, phone, position, ...}]
 *
 * Add   → PATCH department.members (append)
 * Remove→ PATCH department.members (filter)
 * Leader→ PATCH department.members (update is_leader), auto-saved like add/remove
 */
const EmployeesPanel = ({ department, onClose, setSelectedDepartment, setDepartments }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [allUsers, setAllUsers] = useState([]);       // full users list for lookup + search
  const [members, setMembers] = useState([]);          // joined: member entry + user display data
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleCallStart = async (member, mode) => {
    try {
      const response = await axios.post(`${ApiEndPoint}/api/calls/create`, {
        callType: mode,
        invitedUsers: [member.id]
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });

      const roomName = response.data?.data?.roomName;
      if (!roomName) throw new Error("Failed to create call session");

      dispatch(setMeetMode(mode));
      persistMeetModeForNextMeet(mode);
      navigate(`/meets?meeting_id=${encodeURIComponent(roomName)}&meetMode=${mode}`);
    } catch (error) {
      console.error("Call initiation error:", error);
      alert("Failed to start call. Please try again.");
    }
  };

  /** Combobox selection — cleared after each add so placeholder shows again */
  const [addMemberComboValue, setAddMemberComboValue] = useState('');

  // ─── Fetch app users once ───────────────────────────────────────────────────
  useEffect(() => {
    CommanServices.getAppUsers().then(res => {
      if (res.status) setAllUsers(res.data || []);
    }).catch(console.error);
  }, []);

  // ─── Build members list whenever department.members or allUsers changes ─────
  useEffect(() => {
    if (!department) return;
    buildMembers(department.members || [], allUsers);
    setLoading(false);
  }, [department, allUsers]);

  const buildMembers = (rawMembers, users) => {
    const usersById = Object.fromEntries(users.map(u => [u.id, u]));
    const joined = rawMembers.map(m => {
      const user = usersById[m.id] || {};
      const hasActive = !!(user && user.id && (user.name || user.email));
      return {
        id: m.id,
        reporting_enabled: !!m.reporting_enabled,
        is_leader: !!m.is_leader,
        // display fields from users collection (removed users are not in app-users)
        name: hasActive ? (user.name || user.email || '') : UNKNOWN_USER_REMOVED,
        email: user.email || '',
        phone: user.phone || '',
        position: user.position || '',
      };
    });
    setMembers(joined);
  };

  /** Options for Combobox — label packs fields so built-in filter matches name / email / phone / account */
  const addMemberOptions = useMemo(() => {
    if (!department) return [];
    const memberIds = new Set((department.members || []).map((m) => m.id));
    return allUsers
      .filter((u) => !memberIds.has(u.id))
      .sort((a, b) =>
        (a.name || a.email || '').localeCompare(b.name || b.email || '', undefined, {
          sensitivity: 'base',
        })
      )
      .map((u) => {
        const namePart = (u.name || '').trim() || (u.user_account || '').trim() || '';
        const emailPart = (u.email || '').trim() || '';
        let label;
        if (namePart && emailPart) label = `${namePart} — ${emailPart}`;
        else if (emailPart) label = emailPart;
        else label = namePart || 'Unknown';
        return { value: u.id, label };
      });
  }, [allUsers, department]);

  const patchDepartmentMembers = useCallback(async (newRawMembers) => {
    setSaving(true);
    try {
      // Strip out display fields like name, position, email etc to prevent Pydantic 422 errors
      const safeMembersPayload = newRawMembers.map(m => ({
        id: m.id,
        reporting_enabled: m.reporting_enabled,
        is_leader: m.is_leader
      }));

      const res = await CommanServices.updatedepartments(department.id, {
        members: safeMembersPayload,
      });
      // Refresh department object so the rest of the UI stays in sync
      const fresh = await CommanServices.getbyIddepartments(department.id);
      if (fresh.status) {
        const freshDept = fresh.data;
        const membersArr = freshDept.members || [];
        const leaderCount = membersArr.filter((m) => m.is_leader).length;
        const membersVersion = freshDept.membersVersion ?? 0;

        if (setSelectedDepartment) setSelectedDepartment(freshDept);
        if (setDepartments) {
          setDepartments((prev) =>
            prev.map((d) =>
              d.id === department.id
                ? {
                  ...d,
                  members: membersArr,
                  member_count: membersArr.length,
                  leader_count: leaderCount,
                  membersVersion,
                }
                : d
            )
          );
        }
        buildMembers(membersArr, allUsers);

        window.dispatchEvent(
          new CustomEvent('department.updated', {
            detail: { department_id: department.id },
          })
        );
        window.dispatchEvent(
          new CustomEvent('department.members.changed', {
            detail: {
              department_id: department.id,
              leader_count: leaderCount,
              version: membersVersion,
            },
          })
        );
      }
    } catch (err) {
      console.error('Error patching department members:', err);
      const msg = err.response?.data?.detail || err.message;
      alert(`Failed to save changes: ${JSON.stringify(msg)}`);
    } finally {
      setSaving(false);
    }
  }, [department, allUsers, setSelectedDepartment, setDepartments]);

  // ─── Add user ───────────────────────────────────────────────────────────────
  const addUser = async (user) => {
    const already = (department.members || []).some(m => m.id === user.id);
    if (already) { alert('User is already in this department.'); return; }

    const newEntry = { id: user.id, reporting_enabled: true, is_leader: false };
    const updated = [...(department.members || []), newEntry];

    // Optimistic UI
    setMembers(prev => [...prev, {
      id: user.id,
      reporting_enabled: true,
      is_leader: false,
      name: user.name,
      email: user.email || '',
      phone: user.phone || '',
      position: user.position || '',
    }]);
    setAddMemberComboValue('');

    await patchDepartmentMembers(updated);
  };

  // ─── Remove user ────────────────────────────────────────────────────────────
  const removeUser = async (member) => {
    if (!window.confirm(`Remove ${member.name} from this department?`)) return;

    const updated = (department.members || []).filter(m => m.id !== member.id);

    // Optimistic UI
    setMembers(prev => prev.filter(m => m.id !== member.id));

    await patchDepartmentMembers(updated);
  };

  // ─── Toggle leader (multiple leaders allowed; auto-save like add member) ───
  const toggleLeader = async (member, isLeader) => {
    const raw = department.members || [];
    const mid = String(member.id);
    const updated = raw.map((m) =>
      String(m.id) === mid ? { ...m, is_leader: isLeader } : m
    );

    setMembers((prev) =>
      prev.map((m) =>
        String(m.id) === mid ? { ...m, is_leader: isLeader } : m
      )
    );

    await patchDepartmentMembers(updated);
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-2 text-gray-600">Loading members...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Members</h3>
          <p className="text-sm text-gray-600">{department.department_name} · {members.length} member{members.length !== 1 ? 's' : ''}</p>
        </div>
        <Button variant="outline" size="sm" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Add member — same Combobox pattern as Department/Unit name in DepartmentsAndUnits */}
      <div className="border-b pb-4 space-y-1">
        <label className="block text-sm font-medium text-gray-800">Add member</label>
        <Combobox
          value={addMemberComboValue}
          onValueChange={(userId) => {
            if (!userId) {
              setAddMemberComboValue('');
              return;
            }
            const user = allUsers.find(
              (u) => String(u.id) === String(userId)
            );
            if (user) addUser(user);
            else setAddMemberComboValue('');
          }}
          placeholder="Type or select user to add…"
          options={addMemberOptions}
          createLabel="Add user"
          emptyLabel="No users match"
          is_create={false}
          disabled={saving}
          className="border-gray-300"
        />
      </div>

      {/* Members List */}
      <div className="space-y-3">
        {members.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Users className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p>No members yet</p>
            <p className="text-sm">Use the button above to add users</p>
          </div>
        ) : (
          members.map(member => (
            <div key={member.id} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm">{member.name}</div>
                  {member.email && <div className="text-xs text-gray-500">{member.email}</div>}
                  {member.position && <div className="text-xs text-gray-400">{member.position}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-1 text-xs">
                    <span className="text-gray-600">Leader</span>
                    <Switch
                      checked={!!member.is_leader}
                      onCheckedChange={(checked) => toggleLeader(member, checked)}
                      disabled={saving}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCallStart(member, 'video')}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title="Video Call"
                    >
                      <Video className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCallStart(member, 'audio')}
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      title="Audio Call"
                    >
                      <Phone className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeUser(member)}
                    className="text-red-600 hover:text-red-700"
                    disabled={saving}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {saving && (
        <p className="text-xs text-center text-gray-500 animate-pulse">Saving...</p>
      )}
    </div>
  );
};

export default EmployeesPanel;